// "Here is the submittal you were after." A pointer is the page you are looking at,
// sent to someone with a note, landing in their bell as a tap-through link.
//
// It is deliberately not a directive: nothing is assigned, nothing is chased, nothing
// tracks to done. The only thing the sender gets back is whether it was opened — so
// "he's ignoring me" and "he never saw it" stop looking the same.
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; } }
function apiFetch(path, opts = {}) {
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', 'x-worker-token': getToken() || '', ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw d; return d; });
}

// What to call the place being sent. The page's own heading is what the sender just
// read, so it describes the destination better than any route ever would.
function pageLabel() {
  const h = document.querySelector('h1, .admin-page-title');
  const t = (h?.textContent || '').trim().replace(/\s+/g, ' ');
  return t ? t.slice(0, 80) : '';
}

export default function SendPointer({ compact = false, path, label: fixedLabel, tone, push = false }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [picked, setPicked] = useState([]);
  const [q, setQ] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');
  const [label, setLabel] = useState('');

  useEffect(() => {
    if (!open) return;
    setLabel(fixedLabel || pageLabel());
    apiFetch('/users').then(u => setUsers(Array.isArray(u) ? u : [])).catch(() => {});
  }, [open]);

  const me = (() => { try { return JSON.parse(localStorage.getItem('te_worker') || 'null')?.id; } catch { return null; } })();
  const shown = users
    .filter(u => u.id !== me && (u.status === undefined || u.status === 'active'))
    .filter(u => {
      const s = q.trim().toLowerCase();
      return !s || `${u.firstName || ''} ${u.lastName || ''} ${u.role || ''}`.toLowerCase().includes(s);
    })
    .sort((a, b) => `${a.firstName || ''}`.localeCompare(`${b.firstName || ''}`));

  async function send() {
    if (!picked.length) return;
    setBusy(true);
    try {
      const r = await apiFetch('/notifications/send', {
        method: 'POST',
        body: JSON.stringify({ to: picked, path: path || (location.pathname + location.search),
          label: label.trim() || 'a page', note: note.trim() }),
      });
      setDone(`Sent to ${r.sent} ${r.sent === 1 ? 'person' : 'people'}.`);
      setPicked([]); setNote('');
      setTimeout(() => { setOpen(false); setDone(''); }, 1400);
    } catch (e) { setDone(e?.error || 'Could not send that.'); }
    setBusy(false);
  }

  const btn = tone === 'dark'
    ? { background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff',
        cursor: 'pointer', width: 40, height: 40, borderRadius: 20, display: 'flex', alignItems: 'center',
        justifyContent: 'center', flexShrink: 0, padding: 0, marginLeft: push ? 'auto' : 0 }
    : { background: 'none', border: 'none', cursor: 'pointer', padding: 6, color: '#6b7280', display: 'flex', alignItems: 'center' };

  return (
    <>
      <button onClick={() => setOpen(true)} style={btn} aria-label="Send to someone"
        title={fixedLabel ? `Send ${fixedLabel} to someone` : 'Send this page to someone'}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M17 3L9 11M17 3l-5.5 14-2.5-6-6-2.5L17 3z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 6400,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, width: 'min(420px, 96vw)', maxHeight: '84vh',
              display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ fontSize: 15, fontWeight: 800 }}>{fixedLabel ? 'Send this' : 'Send this page'}</div>
              {/* What they will be told they are being sent. Prefilled from the page's
                  own heading where it has one, and editable because plenty of screens
                  have no heading worth reading. */}
              <input value={label} onChange={e => setLabel(e.target.value)} placeholder="What is this? e.g. 2628 submittals"
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 6, padding: '7px 10px', borderRadius: 7,
                  border: '1px solid #e5e7eb', fontSize: 13, color: '#374151' }} />
            </div>

            <div style={{ padding: '10px 16px 0' }}>
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Find a person…"
                style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14 }} />
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 8px 0', minHeight: 120 }}>
              {shown.length === 0 && <div style={{ padding: 16, fontSize: 13, color: '#9ca3af' }}>Nobody matches that.</div>}
              {shown.map(u => {
                const on = picked.includes(u.id);
                return (
                  <button key={u.id}
                    onClick={() => setPicked(p => on ? p.filter(x => x !== u.id) : [...p, u.id])}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                      padding: '9px 10px', border: 'none', borderRadius: 8, cursor: 'pointer',
                      background: on ? '#eef2ff' : 'transparent' }}>
                    <span style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                      border: `1.5px solid ${on ? '#4f46e5' : '#d1d5db'}`, background: on ? '#4f46e5' : '#fff',
                      color: '#fff', fontSize: 12, lineHeight: '16px', textAlign: 'center', fontWeight: 800 }}>{on ? '✓' : ''}</span>
                    <span style={{ fontSize: 13.5, color: '#111', flex: 1 }}>{u.firstName} {u.lastName}</span>
                    <span style={{ fontSize: 11.5, color: '#9ca3af' }}>{u.role}</span>
                  </button>
                );
              })}
            </div>

            <div style={{ padding: '10px 16px 14px', borderTop: '1px solid #f3f4f6' }}>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                placeholder="Add a note — what are they looking at?"
                style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8,
                  border: '1px solid #e5e7eb', fontSize: 14, resize: 'vertical', fontFamily: 'inherit' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                {done && <span style={{ fontSize: 12.5, color: '#15803d', fontWeight: 700 }}>{done}</span>}
                <button onClick={() => setOpen(false)}
                  style={{ marginLeft: 'auto', border: 'none', background: '#f3f4f6', borderRadius: 8,
                    padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                <button onClick={send} disabled={busy || !picked.length}
                  style={{ border: 'none', background: picked.length ? '#e87722' : '#e5e7eb',
                    color: picked.length ? '#fff' : '#9ca3af', borderRadius: 8, padding: '8px 16px',
                    fontSize: 13, fontWeight: 800, cursor: picked.length ? 'pointer' : 'default' }}>
                  {busy ? 'Sending…' : `Send${picked.length ? ` (${picked.length})` : ''}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
